// SKU format: [Type]-[LeftConnectors]-[RightConnectors]-[Length]
// Examples: 
// PSM-XF-XM-10 (10' XLR Female to XLR Male audio cable)
// PSH-XF515P-XMC13-10 (10' XLR Female + 5-15P to XLR Male + IEC C13 hybrid cable)
// PSZ-GH-IJ-10 (10' Zip cable with G,H,I,J connectors)
// PSM-XF^s-XM^s-10 (10' XLR Female to XLR Male audio cable with heat shrink)
// PSM-XF^2-XM^2-10 (10' XLR Female to XLR Male audio cable with red coding rings)

import { connectorList } from './connectorsConfig';
import { findConnectorWithSkuCode } from './utilityService';
import skuCacheService from './skuCacheService';

const {
  parseLengthFeetFromSku,
  resolveLengthFeetForImage,
  isStandardEndToEndStyle
} = require('../utils/cableLengthUtils');

export { parseLengthFeetFromSku, resolveLengthFeetForImage, isStandardEndToEndStyle };

// Standardized cable type codes
const cableTypeCodes = {
  // Audio cables
  PSM: 'PSM', // Mic Cable (3 Poles or more)
  PSI: 'PSI', // Instrument Cable (2 Poles)
  
  // Power cables
  POW: 'PSP', // Power Cable base (will be appended with wire gauge)
  
  // Speaker cables
  PSS: 'PSS', // Speaker Cable base (will be appended with wire gauge)
  
  // Special cables
  PSH: 'PSH', // Hybrid Cable
  PSZ: 'PSZ', // Zip Cable
  PSDMX: 'PSDMX', // DMX Cable
  
  // Legacy mappings for backward compatibility
  microphone: 'PSM',
  mic: 'PSM',
  audio: 'PSM',
  instrument: 'PSI',
  power: 'PSP',
  speaker: 'PSS',
  hybrid: 'PSH',
  zip: 'PSZ',
  dmx: 'PSDT',
  video: 'PSVD'
};

const qualityCodes = {
  standard: 'STD',
  premium: 'PRM',
  elite: 'ELT'
};

// Color mapping for XLR coding rings - using abbreviations for SKU generation
const codingRingColorMap = {
  'black': '', // no append (standard)
  'brown': 'BR',
  'red': 'RD',
  'orange': 'OR',
  'yellow': 'YL',
  'green': 'GR',
  'blue': 'BL',
  'violet': 'VI',
  'grey': 'GY', // Using GY to avoid conflict with green (GR)
  'white': 'WT'
};



// Helper function to get finishing suffix for a connector
// Note: Finishing suffixes (HS, color codes) are no longer included in the official SKU
// but the presence of finishing options still triggers timestamp generation for uniqueness
function getFinishingSuffix(config) {
  // Return empty string - finishing suffixes are not included in official SKU
  // The presence of finishing options is still detected for timestamp generation
  return '';
}

// Utility function to generate timestamp for unique SKU identification
function generateTimestamp() {
  return new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '').replace('T', '-');
}

// Helper function to get finishing suffix for a specific connector
// Note: Finishing suffixes (HS, color codes) are no longer included in the official SKU
// but the presence of finishing options still triggers timestamp generation for uniqueness
function getConnectorFinishingSuffix(config, connectorKey) {
  // Return empty string - finishing suffixes are not included in official SKU
  // The presence of finishing options is still detected for timestamp generation
  return '';
}

// Platinum Series Hybrid Cable Part Numbering System
function getConnectorBlock(conn) {
  const result = findConnectorWithSkuCode(conn);
  return result;
}

// Internal SKU generation function (without caching)
const generateSKUInternal = (config) => {
  // Get finishing suffix for all connectors
  const finishingSuffix = getFinishingSuffix(config);
  
  // Determine type code based on cable style and wire type
  let typeCode = 'PSM'; // Default fallback
  
  // Handle different cable styles
  if (config.style === 'zip' || config.style === 'zip-audio-audio') {
    // Check if this is actually a hybrid cable with wrong style
    if (config.type === 'hybrid') {
      // This is a hybrid cable with wrong style - use PSH
      typeCode = 'PSH';
    } else {
      // True zip cables use PSZ
      typeCode = 'PSZ';
    }
  } else if (config.style === 'wye') {
    // Wye cables always use PSZ (Zip cable type)
    typeCode = 'PSZ';
  } else if (config.style === 'hybrid' || config.style === 'hybrid-power-audio-audio') {
    // Hybrid cables always use PSH
    typeCode = 'PSH';
  } else if (config.type === 'power') {
    // Power cables use PSP + wire gauge
    const wireGauge = config.wireGauge?.gauge || config.powerWireGauge;
    if (wireGauge) {
      const awg = wireGauge.replace('AWG', ''); // Remove 'AWG' suffix if present
      typeCode = `PSP${awg}`;
    } else {
      typeCode = 'PSP'; // Default power cable without specific gauge
    }
  } else if (config.type === 'speaker') {
    // Speaker cables use PSS + wire gauge
    const wireGauge = config.wireGauge?.gauge || config.speakerWireGauge;
    if (wireGauge) {
      const awg = wireGauge.replace('AWG', ''); // Remove 'AWG' suffix if present
      typeCode = `PSS${awg}`;
    } else {
      // Use the wire type from config
      typeCode = config.wireType || 'PSS16';
    }
  } else {
    // For standard audio cables, check if this is a speaker or power cable based on wire type
    console.log('🔍 SKU Service Debug - Standard audio cable logic:', {
      configWireType: config.wireType,
      configAudioWireType: config.audioWireType,
      configType: config.type,
      configStyle: config.style
    });
    
    // If wire type starts with PSS, treat it as a speaker cable
    if (config.wireType && config.wireType.startsWith('PSS')) {
      typeCode = config.wireType; // Use PSS14, PSS12, PSS16 directly
      console.log('🔍 SKU Service: Using PSS wire type:', typeCode);
    } else if (config.wireType && config.wireType.startsWith('PSP')) {
      typeCode = config.wireType; // Use PSP14, PSP12, PSP16 directly
      console.log('🔍 SKU Service: Using PSP wire type:', typeCode);
    } else {
      // For other audio cables, use the wire type from config
      typeCode = config.wireType || config.audioWireType || 'PSM';
      console.log('🔍 SKU Service: Using standard wire type:', typeCode);
    }
  }
  
  const lengthFeet = (() => {
    
    // For hybrid/zip styles, get length from hybridConnectors
    if (['hybrid', 'zip', 'zip-audio-audio', 'wye', 'hybrid-power-audio-audio'].includes(config.style)) {
      const hybridLengths = config.hybridConnectors?.lengths;
      if (hybridLengths) {
        // Try Overall first, then A, convert from inches to feet
        const lengthInches = hybridLengths.Overall || hybridLengths.A || 0;
        // Convert inches to feet and round to nearest inch in feet
        const calculatedLength = Math.round(lengthInches / 12 * 12) / 12;
        return calculatedLength;
      }
    }
    
    // For standard/end-to-end cables, use config.length directly
    if (['standard', 'end-to-end'].includes(config.style) || !config.style) {
      const standardLength = config.length ? Number(config.length) : 0;
      return standardLength;
    }
    
    // Fallback: use config.length directly
    const fallbackLength = config.length ? Number(config.length) : 0;
    return fallbackLength;
  })();
  

  // Note: Timestamps can be optionally added for uniqueness when needed

  // Handle hybrid/zip styles that use hybridConnectors
  if (config.style === 'hybrid' || config.style === 'zip' || config.style === 'zip-audio-audio' || config.style === 'wye' || config.style === 'hybrid-power-audio-audio') {
    const hybridConnectors = config.hybridConnectors?.connectors || {};
    
    // PSH cables are always 14 AWG, so no need to append wire gauge
    // Note: PSH cables use 14 AWG by default, so we don't append the gauge to the SKU
    
    // Hybrid Power+Audio+Audio: Uses I,J (Audio1), K,L (Power), M,N (Audio2)
    // Left side (origin): I, K, M - Right side (destination): J, L, N
    if (config.style === 'hybrid-power-audio-audio') {
      const connI = getConnectorBlock(hybridConnectors.I) + getConnectorFinishingSuffix(config, 'I'); // Audio 1 Left
      const connJ = getConnectorBlock(hybridConnectors.J) + getConnectorFinishingSuffix(config, 'J'); // Audio 1 Right
      const connK = getConnectorBlock(hybridConnectors.K) + getConnectorFinishingSuffix(config, 'K'); // Power Left
      const connL = getConnectorBlock(hybridConnectors.L) + getConnectorFinishingSuffix(config, 'L'); // Power Right
      const connM = getConnectorBlock(hybridConnectors.M) + getConnectorFinishingSuffix(config, 'M'); // Audio 2 Left
      const connN = getConnectorBlock(hybridConnectors.N) + getConnectorFinishingSuffix(config, 'N'); // Audio 2 Right
      const sku = `${typeCode}-${connI}${connK}${connM}-${connJ}${connL}${connN}-${lengthFeet.toFixed(2).replace(/\.00$/, '')}`.toUpperCase();
      return sku;
    }
    
    // Standard Hybrid: Uses G(signal), H(signal), I(power), J(power)
    // Left side (origin): G, I - Right side (destination): H, J
    if (config.style === 'hybrid') {
      const connG = getConnectorBlock(hybridConnectors.G) + getConnectorFinishingSuffix(config, 'G'); // Signal Left
      const connH = getConnectorBlock(hybridConnectors.H) + getConnectorFinishingSuffix(config, 'H'); // Signal Right
      const connI = getConnectorBlock(hybridConnectors.I) + getConnectorFinishingSuffix(config, 'I'); // Power Left
      const connJ = getConnectorBlock(hybridConnectors.J) + getConnectorFinishingSuffix(config, 'J'); // Power Right
      const sku = `${typeCode}-${connG}${connI}-${connH}${connJ}-${lengthFeet.toFixed(2).replace(/\.00$/, '')}`.toUpperCase();
      return sku;
    }
    
    // Zip Audio+Audio: Uses G, H, I, J connectors (all audio)
    // Left side (origin): G, I - Right side (destination): H, J
    if (config.style === 'zip-audio-audio') {
      const connG = getConnectorBlock(hybridConnectors.G) + getConnectorFinishingSuffix(config, 'G'); // Audio Left Top
      const connH = getConnectorBlock(hybridConnectors.H) + getConnectorFinishingSuffix(config, 'H'); // Audio Right Top
      const connI = getConnectorBlock(hybridConnectors.I) + getConnectorFinishingSuffix(config, 'I'); // Audio Left Bottom
      const connJ = getConnectorBlock(hybridConnectors.J) + getConnectorFinishingSuffix(config, 'J'); // Audio Right Bottom
      const sku = `${typeCode}-${connG}${connI}-${connH}${connJ}-${lengthFeet.toFixed(2).replace(/\.00$/, '')}`.toUpperCase();
      return sku;
    }
    
    // Standard Zip: Uses F, G, H, I connectors (all same type)
    // Left side (origin): F, H - Right side (destination): G, I
    if (config.style === 'zip') {
      // Check if this is actually a hybrid cable with wrong style (connectors in G, H, I, J positions)
      if (config.type === 'hybrid' && (hybridConnectors.G?.id || hybridConnectors.H?.id || hybridConnectors.I?.id || hybridConnectors.J?.id)) {
        // This is a hybrid cable with wrong style - use G, H, I, J connectors
        const connG = getConnectorBlock(hybridConnectors.G) + getConnectorFinishingSuffix(config, 'G'); // Left Top
        const connH = getConnectorBlock(hybridConnectors.H) + getConnectorFinishingSuffix(config, 'H'); // Right Top
        const connI = getConnectorBlock(hybridConnectors.I) + getConnectorFinishingSuffix(config, 'I'); // Left Bottom
        const connJ = getConnectorBlock(hybridConnectors.J) + getConnectorFinishingSuffix(config, 'J'); // Right Bottom
        const sku = `${typeCode}-${connG}${connI}-${connH}${connJ}-${lengthFeet.toFixed(2).replace(/\.00$/, '')}`.toUpperCase();
        return sku;
      } else {
        // True zip cable - use F, G, H, I connectors
        const connF = getConnectorBlock(hybridConnectors.F) + getConnectorFinishingSuffix(config, 'F'); // Left Top
        const connG = getConnectorBlock(hybridConnectors.G) + getConnectorFinishingSuffix(config, 'G'); // Right Top
        const connH = getConnectorBlock(hybridConnectors.H) + getConnectorFinishingSuffix(config, 'H'); // Left Bottom
        const connI = getConnectorBlock(hybridConnectors.I) + getConnectorFinishingSuffix(config, 'I'); // Right Bottom
        const sku = `${typeCode}-${connF}${connH}-${connG}${connI}-${lengthFeet.toFixed(2).replace(/\.00$/, '')}`.toUpperCase();
        return sku;
      }
    }
    
    // Wye: Uses E, F, G connectors
    // Single origin: E - Two destinations: F, G
    if (config.style === 'wye') {
      const connE = getConnectorBlock(hybridConnectors.E) + getConnectorFinishingSuffix(config, 'E'); // Single Origin
      const connF = getConnectorBlock(hybridConnectors.F) + getConnectorFinishingSuffix(config, 'F'); // Destination 1
      const connG = getConnectorBlock(hybridConnectors.G) + getConnectorFinishingSuffix(config, 'G'); // Destination 2
      const sku = `${typeCode}-${connE}-${connF}${connG}-${lengthFeet.toFixed(2).replace(/\.00$/, '')}`.toUpperCase();
      return sku;
    }
  }

  // Standard 1:1 cable: Uses endA and endB (for both 'standard' and 'end-to-end' styles)
  if (['standard', 'end-to-end'].includes(config.style) || !config.style) {
    
    const endA = getConnectorBlock(config.connectors?.endA) + getConnectorFinishingSuffix(config, 'endA');
    const endB = getConnectorBlock(config.connectors?.endB) + getConnectorFinishingSuffix(config, 'endB');
    
    
    const sku = `${typeCode}-${endA}-${endB}-${lengthFeet.toFixed(2).replace(/\.00$/, '')}`.toUpperCase();
    return sku;
  }
  
  // Fallback for any unexpected style
  const endA = getConnectorBlock(config.connectors?.endA) + getConnectorFinishingSuffix(config, 'endA');
  const endB = getConnectorBlock(config.connectors?.endB) + getConnectorFinishingSuffix(config, 'endB');
  const sku = `${typeCode}-${endA}-${endB}-${lengthFeet.toFixed(2).replace(/\.00$/, '')}`.toUpperCase();
  return sku;
};

export const parseSKU = (sku) => {
  // Example: PSM-3-MFPMP-PRM-SSN (3' XLR Female Panel-Mount to XLR Male Panel-Mount, Premium, Standard Heat Shrink, Standard TechFlex, No Labels)
  const [typeCode, length, connectors, qualityCode] = sku.split('-');
  
  // Find cable type from code
  const type = Object.entries(cableTypeCodes).find(([_, code]) => code === typeCode)?.[0];
  
  // Parse connectors and variants
  const parseConnectorWithVariant = (code) => {
    let variant = 'inline';
    let baseCode = code;
    if (code.endsWith('P')) {
      variant = 'panel-mount';
      baseCode = code.slice(0, -1);
    }
    const connector = connectorList.find(c => c.skuCode === baseCode) || { type: '', gender: '', brand: '', description: '', variant };
    return { ...connector, variant };
  };
  const endACode = connectors.slice(0, connectors.length / 2);
  const endBCode = connectors.slice(connectors.length / 2);
  const endA = parseConnectorWithVariant(endACode);
  const endB = parseConnectorWithVariant(endBCode);

  // Parse quality
  const quality = Object.entries(qualityCodes).find(([_, code]) => code === qualityCode)?.[0];
  
  return {
    type,
    length: parseFloat(length),
    endA,
    endB,
    quality
  };
};

// Cached SKU generation function (exports the cached version)
export const generateSKU = (config) => {
  return skuCacheService.getOrGenerateSku(config, generateSKUInternal);
};

// Force generate new SKU (for when finishing options change)
export const forceGenerateSKU = (config) => {
  return skuCacheService.forceGenerateSku(config, generateSKUInternal);
};

// Generate SKU with timestamp for uniqueness (used for custom cables)
export const generateSKUWithTimestamp = (config) => {
  // Back-compat: some callers pass a string prefix (e.g. 'PANEL' or 'PANEL-DRAFT').
  // In that case, generate a prefix-based unique id instead of trying to interpret it as a cable config.
  if (typeof config === 'string') {
    const prefix = config.trim() || 'SKU';
    return `${prefix}--${generateTimestamp()}`;
  }

  const baseSku = generateSKUInternal(config);
  const timestamp = generateTimestamp();
  return `${baseSku}--${timestamp}`;
};

/**
 * Panel-specific SKU-ish identifier for captures/uploads.
 * Not an official cable SKU; it's used to name panel proof assets consistently.
 */
export const generatePanelSKUWithTimestamp = (panelConfig) => {
  const size = panelConfig?.size || {};
  const material = String(panelConfig?.material || 'panel').toUpperCase();
  const w = Number(size.width) || 0;
  const h = Number(size.height) || 0;
  const connectors = Array.isArray(panelConfig?.connectors) ? panelConfig.connectors.length : 0;
  const tails = Array.isArray(panelConfig?.wireTailings)
    ? panelConfig.wireTailings.filter((t) => t && !t.noTail).length
    : 0;
  return `PANEL-${material}-${w}X${h}-C${connectors}-T${tails}--${generateTimestamp()}`;
};

// Export cache service for direct access
export { skuCacheService };

// Recipe types and their configurations
export const recipeTypes = {
  single: {
    name: 'Single Cable',
    formula: 'Connector A + Cable + Connector B + Labor',
    components: ['endA', 'cable', 'endB', 'labor']
  },
  dual: {
    name: 'Dual Cable (Zip)',
    formula: 'Connector A + Connector C + Cable + Connector B + Connector D + Labor',
    components: ['endA', 'endC', 'cable', 'endB', 'endD', 'labor']
  },
  wye: {
    name: 'Wye Cable',
    formula: 'Connector A + Connector C + Cable + Connector B + Labor',
    components: ['endA', 'endC', 'cable', 'endB', 'labor']
  }
};

// Cable purposes and their requirements
export const cablePurposes = {
  liveSound: {
    name: 'Live Sound',
    description: 'Professional audio cables for live performances',
    recommendedTypes: ['microphone', 'instrument', 'speaker'],
    recommendedQuality: 'premium'
  },
  studio: {
    name: 'Studio',
    description: 'High-quality cables for studio recording',
    recommendedTypes: ['microphone', 'instrument'],
    recommendedQuality: 'elite'
  },
  installation: {
    name: 'Installation',
    description: 'Durable cables for permanent installation',
    recommendedTypes: ['speaker', 'dmx', 'power'],
    recommendedQuality: 'standard'
  },
  broadcast: {
    name: 'Broadcast',
    description: 'Broadcast-grade cables for video and audio',
    recommendedTypes: ['video', 'microphone', 'dmx'],
    recommendedQuality: 'premium'
  },
  touring: {
    name: 'Touring',
    description: 'Rugged cables for touring applications',
    recommendedTypes: ['microphone', 'instrument', 'speaker', 'power'],
    recommendedQuality: 'premium'
  }
}; 